// prefeitura_frontend/src/pages/admin/activityDashboard/index.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Grid,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Pagination,
  CircularProgress,
  Avatar,
  AvatarGroup,
  Tooltip,
  Alert,
  useMediaQuery,
  useTheme,
  Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CloseIcon from '@mui/icons-material/Close';

import Header from '../../../components/header';
import activityService from '../../../services/activity';
import { Activity, ActivityFilter } from '../../../interfaces/activity';
import { Status, getStatusLabel, getStatusColor } from '../../../enums/Status';
import { useAuth } from '../../../contexts/AuthContext';

import {
  PageContainer,
  PageTitle,
  FilterContainer,
  TableContainer,
  EmptyState,
  StatusChip,
  ActivityCard,
  ActivityCardContent,
  DueDateWarning,
  AssigneesContainer,
  CardsContainer
} from './styles';

const AdminActivityDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  // Verifica se há um projectId na URL
  const searchParams = new URLSearchParams(location.search);
  const projectIdFromURL = searchParams.get('projectId');
  
  // Estados para filtros e paginação
  const [filters, setFilters] = useState<ActivityFilter>({
    search: '',
    status: '',
    dueDateFilter: '',
    projectId: projectIdFromURL || '',
    page: 1,
    pageSize: 9
  });
  
  // Opções de tamanho da página
  const pageSizeOptions = [9, 18, 27];
  
  // Buscar projetos para o seletor
  const [projects, setProjects] = useState<{ id: string, title: string }[]>([]);

  // Efeito para carregar a lista de projetos (simplificado para este exemplo)
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // Aqui você chamaria um serviço para obter os projetos
        // Exemplo: const result = await projectService.getAllProjects();
        // setProjects(result);

        // Por enquanto, vamos apenas verificar se temos um projectId na URL
        // e usar isso para criar um "projeto selecionado"
        if (projectIdFromURL) {
          setProjects([{ id: projectIdFromURL, title: 'Projeto Atual' }]);
        }
      } catch (err) {
        console.error('Erro ao buscar projetos:', err);
      }
    };

    fetchProjects();
  }, [projectIdFromURL]);
  
  // Estado para armazenar as atividades
  const [activities, setActivities] = useState<Activity[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Estado de carregamento
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Estado para exibir/ocultar filtros em dispositivos móveis
  const [showFilters, setShowFilters] = useState(!isMobile);

  // Mapear status para cores
  const getStatusColorFromString = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'todo':
        return '#6E7681'; // Cinza
      case 'inprogress':
        return '#3593FF'; // Azul
      case 'review':
        return '#FF8B3E'; // Laranja
      case 'done':
        return '#4CAF50'; // Verde
      case 'blocked':
        return '#E74C3C'; // Vermelho
      default:
        return '#6E7681'; // Cinza padrão
    }
  };
  
  // Formatar data
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Sem prazo';
    
    const date = new Date(dateString);
    const today = new Date();
    
    // Se a data for o dia atual, mostramos "Hoje" em vez da data
    if (date.getDate() === today.getDate() && 
        date.getMonth() === today.getMonth() && 
        date.getFullYear() === today.getFullYear()) {
      return 'Hoje';
    }
    
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };
  
  // Buscar atividades
  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await activityService.getFilteredActivities(filters);
      
      setActivities(result.activities);
      setTotalPages(result.totalPages);
      setTotalCount(result.totalCount);
    } catch (err) {
      console.error('Erro ao buscar atividades:', err);
      setError('Não foi possível carregar as atividades. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };
  
  // Efeito para carregar inicialmente e quando mudam os filtros
  useEffect(() => {
    fetchActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.pageSize]);
  
  // Atualizar filtros
  const handleFilterChange = (name: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1 // Voltar para a primeira página ao alterar filtros
    }));
  };
  
  // Aplicar filtros
  const handleApplyFilters = () => {
    fetchActivities();
    if (isMobile) {
      setShowFilters(false);
    }
  };
  
  // Limpar filtros
  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      dueDateFilter: '',
      projectId: projectIdFromURL || '',  // Mantém o projectId se for acessado a partir do projeto
      page: 1,
      pageSize: 9
    });
    
    // Buscar sem filtros
    setTimeout(fetchActivities, 0);
  };
  
  // Navegar para página do card
  const handleCardClick = (activity: Activity) => {
    navigate(`/project/${activity.projectId}`, { 
      state: { 
        openCardId: activity.id,
        boardId: activity.boardId,
        columnId: activity.columnId // Adicionando columnId para navegação precisa
      } 
    });
  };
  
  // Renderizar cor e mensagem de prazo
  const renderDueInfo = (activity: Activity) => {
    if (!activity.dueDate) return null;
    
    // Cards with status 'Done' should not show due date warnings
    if (activity.status === 'Done') return null;
    
    if (activity.isOverdue) {
      return (
        <DueDateWarning isOverdue={true}>
          <EventBusyIcon sx={{ fontSize: 14 }} />
          <span>Atrasado há {Math.abs(activity.daysUntilDue)} dias</span>
        </DueDateWarning>
      );
    }
    
    return (
      <DueDateWarning isOverdue={false}>
        <AccessTimeIcon sx={{ fontSize: 14 }} />
        <span>{activity.daysUntilDue} dias restantes</span>
      </DueDateWarning>
    );
  };
  
  return (
    <>
      <Header title="Painel de Atividades" />
      
      <PageContainer maxWidth="xl" sx={{ 
        mt: 8, 
        pb: 2, 
        display: 'flex', 
        flexDirection: 'column', 
        height: 'calc(100vh - 64px)', 
        overflow: 'hidden'
      }}>
        {/* Botão móvel para mostrar/ocultar filtros - fora da área de scroll */}
        {isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button 
              variant="outlined" 
              onClick={() => setShowFilters(!showFilters)}
              startIcon={<FilterListIcon />}
            >
              {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
            </Button>
          </Box>
        )}
        
        <Box sx={{
          flexGrow: 1,
          overflowY: 'auto',
          width: '100%',
          height: '100%',
          paddingRight: '12px', // Adicionando padding à direita para afastar a scrollbar
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(255, 255, 255, 0.2)',
          },
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255, 255, 255, 0.1) transparent'
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            width: '100%',
            paddingLeft: '2px',
            paddingRight: '2px'
          }}>
          {/* Filtros - agora em uma seção separada */}
          {showFilters && (
            <FilterContainer elevation={1} sx={{ mb: 2, backgroundColor: 'rgba(21, 32, 43, 0.6)', mx: '6px' }}>
              <Grid container spacing={2} alignItems="center">
                {/* Campo de busca por nome */}
                <Grid item xs={12} sm={6} md={projectIdFromURL ? 3 : 2.5}>
                  <TextField
                    fullWidth
                    placeholder="Buscar por nome"
                    variant="outlined"
                    value={filters.search || ''}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(21, 31, 48, 0.7)',
                        borderRadius: '4px'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <SearchIcon color="action" />
                      )
                    }}
                  />
                </Grid>
                
                {/* Filtro de status */}
                <Grid item xs={12} sm={6} md={projectIdFromURL ? 3 : 2.5}>
                  <FormControl fullWidth variant="outlined">
                    <Select
                      displayEmpty
                      value={filters.status || ''}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      renderValue={(selected) => {
                        if (selected === '') {
                          return 'Status';
                        }
                        const statusMap: Record<string, string> = {
                          'Todo': 'A Fazer',
                          'InProgress': 'Em Progresso',
                          'Review': 'Revisão',
                          'Done': 'Concluído',
                          'Blocked': 'Bloqueado'
                        };
                        return statusMap[selected] || selected;
                      }}
                      sx={{ 
                        backgroundColor: 'rgba(21, 31, 48, 0.7)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.1)'
                        },
                        borderRadius: '4px'
                      }}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value={Status.TODO.toString()}>A Fazer</MenuItem>
                      <MenuItem value={Status.IN_PROGRESS.toString()}>Em Progresso</MenuItem>
                      <MenuItem value={Status.REVIEW.toString()}>Revisão</MenuItem>
                      <MenuItem value={Status.DONE.toString()}>Concluído</MenuItem>
                      <MenuItem value={Status.BLOCKED.toString()}>Bloqueado</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Filtro de prazo */}
                <Grid item xs={12} sm={6} md={projectIdFromURL ? 3 : 2.5}>
                  <FormControl fullWidth variant="outlined">
                    <Select
                      displayEmpty
                      value={filters.dueDateFilter || ''}
                      onChange={(e) => handleFilterChange('dueDateFilter', e.target.value)}
                      renderValue={(selected) => {
                        if (selected === '') {
                          return 'Prazo';
                        }
                        const dueDateMap: Record<string, string> = {
                          'overdue': 'Em atraso',
                          'next7days': 'Próximos 7 dias',
                          'next15days': 'Próximos 15 dias',
                          'noduedate': 'Sem prazo'
                        };
                        return dueDateMap[selected] || selected;
                      }}
                      sx={{ 
                        backgroundColor: 'rgba(21, 31, 48, 0.7)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.1)'
                        },
                        borderRadius: '4px'
                      }}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value="overdue">Em atraso</MenuItem>
                      <MenuItem value="next7days">Próximos 7 dias</MenuItem>
                      <MenuItem value="next15days">Próximos 15 dias</MenuItem>
                      <MenuItem value="noduedate">Sem prazo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Filtro de projeto - exibido apenas quando não vem de um projeto específico */}
                {!projectIdFromURL && (
                  <Grid item xs={12} sm={6} md={2.5}>
                    <FormControl fullWidth variant="outlined">
                      <Select
                        displayEmpty
                        value={filters.projectId || ''}
                        onChange={(e) => handleFilterChange('projectId', e.target.value)}
                        renderValue={(selected) => {
                          if (selected === '') {
                            return 'Projeto';
                          }
                          const selectedProject = projects.find(p => p.id === selected);
                          return selectedProject ? selectedProject.title : 'Projeto';
                        }}
                        sx={{ 
                          backgroundColor: 'rgba(21, 31, 48, 0.7)',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          borderRadius: '4px'
                        }}
                      >
                        <MenuItem value="">Todos</MenuItem>
                        {projects.map((project) => (
                          <MenuItem key={project.id} value={project.id}>
                            {project.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                
                {/* Botões de ação */}
                <Grid item xs={12} sm={12} md={projectIdFromURL ? 3 : 2} sx={{ 
                  display: 'flex', 
                  justifyContent: { xs: 'flex-end', md: 'flex-end' } 
                }}>
                  <Box display="flex" gap={1}>
                    <Button
                      variant="outlined"
                      onClick={handleClearFilters}
                      size="medium"
                      sx={{ 
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        color: '#fff', 
                        '&:hover': {
                          borderColor: 'rgba(255, 255, 255, 0.4)',
                        },
                        borderRadius: '4px',
                        textTransform: 'none'
                      }}
                      startIcon={<CloseIcon />}
                    >
                      Limpar
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleApplyFilters}
                      size="medium"
                      sx={{ 
                        backgroundColor: '#3498db',
                        '&:hover': {
                          backgroundColor: '#2980b9',
                        },
                        borderRadius: '4px',
                        textTransform: 'none'
                      }}
                      startIcon={<FilterListIcon />}
                    >
                      Filtrar
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </FilterContainer>
          )}
          
          {/* Informações de resultados */}
          <Box mb={1} sx={{ 
            py: 1,
            px: '6px'
          }}>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem', mb: 1 }}>
              {totalCount === 0 
                ? 'Nenhuma atividade encontrada' 
                : totalCount === 1
                  ? 'Mostrando 1 de 1 atividade'
                  : `Mostrando ${activities.length > 0 ? (filters.page - 1) * filters.pageSize + 1 : 0} a ${Math.min(filters.page * filters.pageSize, totalCount)} de ${totalCount} atividades`}
            </Typography>
          </Box>
          
          {/* Mensagem de erro */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {/* Container dos cards */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            mb: 2,
            width: '100%'
          }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: '200px' }}>
                <CircularProgress size={48} />
              </Box>
            ) : activities.length === 0 ? (
              <EmptyState>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Nenhuma atividade encontrada
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Tente alterar os filtros ou adicionar novas atividades aos projetos
                </Typography>
              </EmptyState>
            ) : (
              <CardsContainer>
                <Grid 
                  container 
                  spacing={2} 
                  sx={{ 
                    margin: 0,
                    width: '100%',
                    paddingX: '6px' // Adiciona um padding lateral para melhor alinhamento
                  }}
                >
                  {activities.map((activity) => (
                    <Grid 
                      item 
                      xs={12} 
                      sm={6} 
                      md={4} 
                      key={activity.id} 
                      sx={{ 
                        padding: '8px'
                      }}
                    >
                      <ActivityCard onClick={() => handleCardClick(activity)}>
                        <ActivityCardContent>
                          {/* Cabeçalho do Card com Título e Status */}
                          <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                              <Box sx={{ width: 'calc(100% - 80px)' }}>
                                <Typography variant="h6" noWrap sx={{ 
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis',
                                  fontWeight: 'bold',
                                  mb: 0.5,
                                  fontSize: '0.9rem',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {activity.name}
                                </Typography>
                              </Box>
                              <StatusChip color={getStatusColorFromString(activity.status)} sx={{ minWidth: '70px' }}>
                                {activity.status === 'Todo' ? 'A Fazer' :
                                activity.status === 'InProgress' ? 'Em Progresso' :
                                activity.status === 'Review' ? 'Revisão' :
                                activity.status === 'Done' ? 'Concluído' :
                                activity.status === 'Blocked' ? 'Bloqueado' : activity.status}
                              </StatusChip>
                            </Box>
                            
                            {/* Informações do Projeto */}
                            <Box sx={{ mt: 0.5, color: '#adbac7' }}>
                              <Typography variant="body2" sx={{ 
                                opacity: 0.8, 
                                fontSize: '0.75rem', 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap'
                              }}>
                                Projeto: {activity.projectName}
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                opacity: 0.8, 
                                mt: 0.5, 
                                fontSize: '0.75rem', 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap'
                              }}>
                                Quadro: {activity.boardName} • Coluna: {activity.columnName}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Parte central flexível */}
                          <Box sx={{ flex: 1 }} />
                          
                          {/* Parte inferior fixa */}
                          <Box>
                            {/* Data de Prazo */}
                            <Box mt={1.5}>
                              <Typography variant="body2" sx={{ 
                                color: '#adbac7', 
                                fontWeight: 'medium', 
                                fontSize: '0.75rem' 
                              }}>
                                Prazo: {formatDate(activity.dueDate)}
                              </Typography>
                              {renderDueInfo(activity)}
                            </Box>
                            
                            {/* Responsáveis */}
                            <AssigneesContainer>
                              <Typography variant="body2" sx={{ color: '#adbac7', fontSize: '0.75rem' }}>
                                Responsáveis:
                              </Typography>
                              {activity.assignees && activity.assignees.length > 0 ? (
                                <AvatarGroup max={3} sx={{ 
                                  ml: 1, 
                                  '& .MuiAvatar-root': { 
                                    width: 20, 
                                    height: 20, 
                                    fontSize: '0.7rem' 
                                  } 
                                }}>
                                  {activity.assignees.map((assignee) => (
                                    <Tooltip 
                                      key={assignee.id} 
                                      title={`${assignee.firstName} ${assignee.lastName}`}
                                    >
                                      <Avatar sx={{ width: 20, height: 20, fontSize: '0.7rem' }}>
                                        {assignee.firstName[0]}{assignee.lastName[0]}
                                      </Avatar>
                                    </Tooltip>
                                  ))}
                                </AvatarGroup>
                              ) : (
                                <Typography variant="body2" sx={{ 
                                  ml: 1, 
                                  color: '#adbac7', 
                                  opacity: 0.7, 
                                  fontSize: '0.75rem' 
                                }}>
                                  Nenhum
                                </Typography>
                              )}
                            </AssigneesContainer>
                          </Box>
                        </ActivityCardContent>
                      </ActivityCard>
                    </Grid>
                  ))}
                </Grid>
              </CardsContainer>
            )}
          </Box>
          
          {/* Paginação */}
          {activities.length > 0 && totalPages > 1 && (
          <Paper 
          elevation={0}
          sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          py: 1.5,
          px: 2,
          mt: 2,
          mx: '6px',
          backgroundColor: 'transparent',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
            width: 'calc(100% - 12px)'
            }}
              >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Pagination
                  count={totalPages}
                  page={filters.page}
                  onChange={(e, page) => setFilters(prev => ({ ...prev, page }))}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  size={isMobile ? "small" : "medium"}
                  sx={{ 
                    '& .MuiPaginationItem-root': {
                      color: '#adbac7',
                      '&.Mui-selected': {
                        backgroundColor: '#3498db',
                        color: 'white',
                      }
                    }
                  }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: '#adbac7', mr: 1 }}>Por página:</Typography>
                <FormControl variant="outlined" size="small" sx={{ minWidth: '80px' }}>
                  <Select
                    value={filters.pageSize}
                    onChange={(e) => {
                      const newPageSize = Number(e.target.value);
                      setFilters(prev => ({ 
                        ...prev, 
                        pageSize: newPageSize,
                        // Recalcular a página atual para não perder a posição aproximada
                        page: Math.floor((prev.page - 1) * prev.pageSize / newPageSize) + 1
                      }));
                    }}
                    sx={{ 
                      backgroundColor: 'rgba(21, 31, 48, 0.7)',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.1)'
                      },
                      height: '32px'
                    }}
                    displayEmpty
                  >
                    {pageSizeOptions.map(size => (
                      <MenuItem key={size} value={size}>{size}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Paper>
          )}
          </Box>
        </Box>
      </PageContainer>
    </>
  );
};

export default AdminActivityDashboard;